import { useState, useEffect, ReactNode } from "react";
import { LoadingOverlay } from "./LoadingOverlay";
import { loadingService, type LoaderType } from "./loading.service";
import { LoadingContext } from "./LoadingContext";

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider = ({ children }: LoadingProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loaderType, setLoaderType] = useState<LoaderType>("central");
  const [inlineLoadingElement, setInlineLoadingElement] = useState<string | null>(null);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [subtitle, setSubtitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Subscribe to global loading service
    const unsubscribe = loadingService.subscribe((loading, type, msg, sub) => {
      setIsLoading(loading);
      setLoaderType(type || "central");
      setMessage(msg);
      setSubtitle(sub);
    });

    // Subscribe to inline loading service
    const unsubscribeInline = loadingService.subscribeInline((elementId) => {
      setInlineLoadingElement(elementId);
    });

    return () => {
      unsubscribe();
      unsubscribeInline();
    };
  }, []);

  const setLoading = (
    loading: boolean,
    type: LoaderType = "central",
    elementId?: string,
    msg?: string,
    sub?: string
  ) => {
    if (loading) {
      loadingService.show(type, elementId, msg, sub);
    } else {
      loadingService.hide();
    }
  };

  const handleDismiss = () => {
    loadingService.reset();
  };

  return (
    <LoadingContext.Provider
      value={{ isLoading, loaderType, inlineLoadingElement, message, subtitle, setLoading }}
    >
      {children}
      <LoadingOverlay
        isVisible={isLoading && loaderType === "central"}
        message={message}
        subtitle={subtitle}
        onDismiss={handleDismiss}
      />
    </LoadingContext.Provider>
  );
};
